/**
 * Attribution params appended to every outbound shop link (fender.com and the sibling
 * brand storefronts) so the sale is credited back to MyFender in Fender.com analytics.
 *
 * Keep the values in sync with the equivalent constant in paeapi
 * (`buildShopSearchUrl` in `src/libraries/fenderProducts.ts`), which stamps the same
 * params onto the cross-sell `shopUrl`s it returns.
 */
export const SHOP_ATTRIBUTION_PARAMS: ReadonlyArray<[string, string]> = [
  ["utm_source", "Fender"],
  ["utm_medium", "my_fender"],
];

/** Pre-built query string (no leading `?`), for hardcoded hrefs in templates. */
export const SHOP_ATTRIBUTION_QUERY = SHOP_ATTRIBUTION_PARAMS.map(([k, v]) => `${k}=${v}`).join("&");

/**
 * Appends the shop attribution params to a storefront URL, preserving any existing
 * query string and hash and skipping params the URL already carries (so a URL that
 * has already been stamped — or one an admin authored with its own utm_source — is
 * left alone).
 *
 * Deliberately string-based rather than `new URL()`: call sites pass both absolute
 * URLs and base+route concatenations, and this runs in the NativeScript runtime too.
 */
export function withShopAttribution(url: string): string {
  if (!url) return url;
  const hashIndex = url.indexOf("#");
  let base = hashIndex > -1 ? url.substring(0, hashIndex) : url;
  const hash = hashIndex > -1 ? url.substring(hashIndex) : "";
  for (const [key, value] of SHOP_ATTRIBUTION_PARAMS) {
    if (new RegExp(`[?&]${key}=`).test(base)) continue;
    base += (base.indexOf("?") > -1 ? "&" : "?") + `${key}=${value}`;
  }
  return base + hash;
}
